import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Orbit, ArrowLeft, Mail, Loader2, Video, Globe2, Languages, ShieldCheck } from "lucide-react";
import { LiveTiles } from "@/components/LiveTiles";

export function nextRoute(u) {
  if (u.role === "admin") return "/admin";
  if (u.email_verified === false) return "/verify-email";
  return u.onboarded ? "/match" : "/onboarding";
}

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.9-5.4 3.9-3.3 0-5.9-2.7-5.9-6s2.6-6 5.9-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z" />
  </svg>
);

const inputCls = "w-full mt-1 px-4 py-3 rounded-xl bg-slate-950/60 border border-white/10 focus:border-cyan-400 outline-none text-slate-100";

export default function Auth() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // email | password | forgot | sent
  const [exists, setExists] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav(nextRoute(user), { replace: true }); }, [user, nav]);

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const continueEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email/check", { email });
      if (data.google_only) { toast.info("This account uses Google — tap Continue with Google"); return; }
      setExists(data.exists);
      setStep("password");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = exists ? await login(email, password) : await register({ email, password });
      toast.success(exists ? "Welcome back" : "Welcome to Orbit Hello");
      nav(nextRoute(u), { replace: true });
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setStep("sent"); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const submitLabel = exists ? "Sign in" : "Create account";
  return (
    <div className="min-h-screen orbit-bg-grid" data-testid="auth-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-0 lg:min-h-screen grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <aside className="lg:col-span-7 orbit-fade-up" data-testid="auth-showcase">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="auth-brand">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
              <Orbit className="w-5 h-5 text-slate-950" strokeWidth={3} />
            </div>
            <span className="font-display text-2xl font-extrabold">ORBIT<span className="orbit-gradient-text">HELLO</span></span>
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] mt-6">
            Random video chat<br /><span className="orbit-gradient-text">with real people</span> worldwide.
          </h1>
          <p className="text-slate-300 mt-4 max-w-lg text-base">Tap once and you're face-to-face with someone new — from 190+ countries. Skip anytime, chat by text, break the ice with AI, and stay safe with 24/7 moderation.</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3 max-w-xl text-sm">
            {[[Video, "1-on-1 live video, no downloads"], [Globe2, "Match by interest, language or country"], [Languages, "Live translated captions"], [ShieldCheck, "Verified accounts & AI moderation"]].map(([Icon, t]) => (
              <li key={t} className="flex items-center gap-3 orbit-glass rounded-xl px-3 py-2.5"><Icon className="w-4 h-4 text-cyan-300 shrink-0" /><span className="text-slate-200">{t}</span></li>
            ))}
          </ul>
          <div className="mt-6 hidden sm:block">
            <div className="relative rounded-2xl orbit-glass p-2 max-w-md">
              <LiveTiles className="rounded-xl overflow-hidden aspect-[16/10] grid-rows-2" />
              <div className="absolute top-4 left-4 z-10 orbit-chip active !bg-emerald-500/10 !border-emerald-500/40 !text-emerald-300">● LIVE NOW</div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-5 w-full max-w-sm mx-auto lg:mx-0 lg:ml-auto">
        <div className="orbit-glass rounded-3xl p-7 sm:p-8 orbit-fade-up" style={{ animationDelay: "120ms" }}>
          {step === "email" && (
            <>
              <h1 className="font-display text-2xl font-bold text-center">Meet the world</h1>
              <p className="text-slate-400 text-sm text-center mt-1 mb-6">One tap and you're in. Nothing else to fill.</p>
              <button onClick={google} className="w-full flex items-center justify-center gap-3 py-3 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors" data-testid="auth-google-btn">
                <GoogleIcon /> Continue with Google
              </button>
              <div className="flex items-center gap-3 my-5 text-[11px] font-mono uppercase text-slate-500"><span className="flex-1 h-px bg-white/10" />or<span className="flex-1 h-px bg-white/10" /></div>
              <form onSubmit={continueEmail} className="space-y-3">
                <input required type="email" autoFocus placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="auth-email-input" />
                <button type="submit" disabled={loading} className="orbit-btn-primary w-full flex items-center justify-center gap-2" data-testid="auth-email-continue-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Continue with email
                </button>
              </form>
            </>
          )}

          {step === "password" && (
            <form onSubmit={submitPassword} className="space-y-4">
              <button type="button" onClick={() => { setStep("email"); setPassword(""); }} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="auth-back-btn"><ArrowLeft className="w-3 h-3" /> {email}</button>
              <h1 className="font-display text-2xl font-bold" data-testid="auth-password-title">{exists ? "Welcome back" : "Create your password"}</h1>
              <p className="text-slate-400 text-sm -mt-2">{exists ? "Enter your password to continue." : "New here? Pick a password (6+ characters) and you're in."}</p>
              <input required autoFocus type="password" minLength={6} placeholder={exists ? "Password" : "Create a password"} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="auth-password-input" />
              <button type="submit" disabled={loading} className="orbit-btn-primary w-full" data-testid="auth-submit-btn">
                {loading ? "…" : submitLabel}
              </button>
              {exists && <button type="button" onClick={() => setStep("forgot")} className="w-full text-center text-xs text-cyan-300 hover:text-cyan-200" data-testid="auth-forgot-link">Forgot password?</button>}
            </form>
          )}

          {step === "forgot" && (
            <form onSubmit={sendReset} className="space-y-4">
              <button type="button" onClick={() => setStep("password")} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="auth-forgot-back-btn"><ArrowLeft className="w-3 h-3" /> Back</button>
              <h1 className="font-display text-2xl font-bold">Reset your password</h1>
              <p className="text-slate-400 text-sm -mt-2">We'll email you a secure link that works once and expires in 60 minutes.</p>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="auth-forgot-email-input" />
              <button type="submit" disabled={loading} className="orbit-btn-primary w-full" data-testid="auth-forgot-submit-btn">{loading ? "…" : "Send reset link"}</button>
            </form>
          )}

          {step === "sent" && (
            <div className="text-center" data-testid="auth-forgot-sent">
              <div className="w-14 h-14 mx-auto rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center"><Mail className="w-6 h-6 text-cyan-300" /></div>
              <h1 className="font-display text-2xl font-bold mt-4">Check your inbox</h1>
              <p className="text-slate-400 text-sm mt-2">If an account exists for <span className="text-slate-100 font-semibold">{email}</span>, a reset link is on its way.</p>
              <button onClick={() => setStep("email")} className="orbit-btn-primary w-full mt-6" data-testid="auth-forgot-done-btn">Back to sign in</button>
            </div>
          )}
        </div>
        <p className="mt-6 text-xs text-slate-500 text-center">By continuing you agree to Orbit Hello's <Link to="/safety" className="text-slate-300 hover:text-cyan-300">guidelines</Link>. 18+ only.</p>
        </div>
      </div>
    </div>
  );
}
